import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Check, ChevronsUpDown, Settings2 } from "lucide-react";
import { environmentsApi } from "@/api/environments";
import { useEnvStore } from "@/stores/env";
import { cn } from "@/lib/cn";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";

const TYPE_DOT: Record<string, string> = {
  production: "bg-brand",
  development: "bg-wait",
  custom: "bg-muted-foreground",
};

/** Top-nav environment switcher. Switching re-scopes every request (via the
 * X-Deleggate-Env header) and refetches all env-scoped data. */
export function EnvSwitcher() {
  const qc = useQueryClient();
  const environments = useEnvStore((s) => s.environments);
  const activeSlug = useEnvStore((s) => s.activeSlug);
  const setActive = useEnvStore((s) => s.setActive);
  const setEnvironments = useEnvStore((s) => s.setEnvironments);

  const q = useQuery({ queryKey: ["environments"], queryFn: () => environmentsApi.list() });
  useEffect(() => {
    if (q.data) setEnvironments(q.data.data, q.data.limit, q.data.unlimited);
  }, [q.data, setEnvironments]);

  const active = environments.find((e) => e.slug === activeSlug);

  const switchTo = (slug: string) => {
    if (slug === activeSlug) return;
    setActive(slug);
    qc.invalidateQueries(); // everything below is env-scoped
  };

  if (environments.length === 0) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="flex items-center gap-2 rounded-full border bg-background/60 py-1 pl-2.5 pr-2 text-xs outline-none transition-colors hover:bg-background focus-visible:ring-2 focus-visible:ring-ring">
        <span className={cn("h-2 w-2 rounded-full", TYPE_DOT[active?.type ?? "custom"])} />
        <span className="font-medium text-foreground">{active?.name ?? "Environment"}</span>
        <ChevronsUpDown size={13} className="text-muted-foreground" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56">
        <DropdownMenuLabel className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
          Environment
        </DropdownMenuLabel>
        {environments.map((e) => (
          <DropdownMenuItem key={e.id} onClick={() => switchTo(e.slug)} className="gap-2">
            <span className={cn("h-2 w-2 rounded-full", TYPE_DOT[e.type])} />
            <span className="flex-1 truncate">{e.name}</span>
            {e.slug === activeSlug && <Check size={14} className="text-brand" />}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild className="gap-2 text-muted-foreground">
          {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
          <Link to={"/settings" as any}>
            <Settings2 size={14} /> Manage environments
          </Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
